import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import type { PermissionRoleMenuOption } from '@affine/core/modules/permissions/components/permission-role-menu';
import { DocRole, FolderRole, Permission } from '@affine/graphql';
import type { useI18n } from '@affine/i18n';
import { useMemo } from 'react';

import { objectRoleLabel } from './object-role-labels';

type TFunc = ReturnType<typeof useI18n>;

export interface ObjectPermissionOnlineOnlyState {
  disabled: boolean;
  offline: boolean;
  mode: 'online-only';
}

export function useObjectPermissionOnlineOnlyState(options?: {
  backendUnavailable?: boolean;
}): ObjectPermissionOnlineOnlyState {
  const connectivityStatus = useConnectivityStatus();
  const connectivityOffline = useUiOfflineSignal(connectivityStatus.offline);
  const backendUnavailable = options?.backendUnavailable === true;
  const offline = connectivityOffline || backendUnavailable;

  return useMemo(
    () => ({
      disabled: offline,
      offline,
      mode: 'online-only' as const,
    }),
    [offline]
  );
}

export interface ObjectPermissionAreaBlockedEntry {
  isAreaBlocked?: boolean | null;
  areaBoundaryKind?: string | null;
  areaBoundaryRootName?: string | null;
}

export const DOC_SHARE_MEMBER_ROLES: readonly DocRole[] = [
  DocRole.Manager,
  DocRole.Editor,
  DocRole.Reader,
];

export const DOC_GUEST_INVITE_ROLES: readonly DocRole[] = [DocRole.Reader];

const WORKSPACE_DOC_ROLE_CEILING: Record<Permission, DocRole> = {
  [Permission.Owner]: DocRole.Owner,
  [Permission.Admin]: DocRole.Owner,
  [Permission.Editor]: DocRole.Manager,
  [Permission.Reader]: DocRole.Reader,
};

const DOC_ROLE_RANK: Record<DocRole, number> = {
  [DocRole.None]: 0,
  [DocRole.Reader]: 1,
  [DocRole.Commenter]: 2,
  [DocRole.Editor]: 3,
  [DocRole.Manager]: 4,
  [DocRole.Owner]: 5,
};

const WORKSPACE_FOLDER_ROLE_CEILING: Record<Permission, FolderRole> = {
  [Permission.Owner]: FolderRole.Owner,
  [Permission.Admin]: FolderRole.Owner,
  [Permission.Editor]: FolderRole.Admin,
  [Permission.Reader]: FolderRole.Reader,
};

const FOLDER_ROLE_RANK: Record<FolderRole, number> = {
  [FolderRole.None]: 0,
  [FolderRole.Reader]: 1,
  [FolderRole.Editor]: 2,
  [FolderRole.Admin]: 3,
  [FolderRole.Owner]: 4,
};

export function isDocRoleWithinWorkspaceCeiling(
  role: DocRole,
  workspaceRole: Permission
): boolean {
  return (
    DOC_ROLE_RANK[role] <=
    DOC_ROLE_RANK[WORKSPACE_DOC_ROLE_CEILING[workspaceRole]]
  );
}

export function createWorkspaceCappedDocRoleOptions(
  t: TFunc,
  roles: readonly DocRole[],
  workspaceRole: Permission
): PermissionRoleMenuOption<DocRole>[] {
  return createObjectPermissionRoleOptions(t, roles).map(option => ({
    ...option,
    disabled: !isDocRoleWithinWorkspaceCeiling(option.role, workspaceRole),
    disabledReason: !isDocRoleWithinWorkspaceCeiling(option.role, workspaceRole)
      ? t['com.streamlain.permissions.workspace-role-not-covered']()
      : undefined,
  }));
}

export function createWorkspaceCappedFolderRoleOptions(
  t: TFunc,
  roles: readonly FolderRole[],
  workspaceRole: Permission
): PermissionRoleMenuOption<FolderRole>[] {
  const ceiling = WORKSPACE_FOLDER_ROLE_CEILING[workspaceRole];
  return createObjectPermissionRoleOptions(t, roles).map(option => ({
    ...option,
    disabled: FOLDER_ROLE_RANK[option.role] > FOLDER_ROLE_RANK[ceiling],
    disabledReason:
      FOLDER_ROLE_RANK[option.role] > FOLDER_ROLE_RANK[ceiling]
        ? t['com.streamlain.permissions.workspace-role-not-covered']()
        : undefined,
  }));
}

export function isFolderRoleWithinWorkspaceCeiling(
  role: FolderRole,
  workspaceRole: Permission
): boolean {
  const ceiling = WORKSPACE_FOLDER_ROLE_CEILING[workspaceRole];
  return FOLDER_ROLE_RANK[role] <= FOLDER_ROLE_RANK[ceiling];
}

export function isDocGroupRolePartiallyLimited(
  role: DocRole,
  memberWorkspaceRoles: readonly Permission[]
): boolean {
  return memberWorkspaceRoles.some(
    workspaceRole => !isDocRoleWithinWorkspaceCeiling(role, workspaceRole)
  );
}

export function isFolderGroupRolePartiallyLimited(
  role: FolderRole,
  memberWorkspaceRoles: readonly Permission[]
): boolean {
  return memberWorkspaceRoles.some(
    workspaceRole => !isFolderRoleWithinWorkspaceCeiling(role, workspaceRole)
  );
}

export function getWorkspaceRoleCeilingNotice(
  t: TFunc,
  workspaceRole: Permission,
  roles: readonly DocRole[]
): string | null {
  if (
    roles.every(role => isDocRoleWithinWorkspaceCeiling(role, workspaceRole))
  ) {
    return null;
  }
  return t['com.streamlain.permissions.workspace-role-ceiling']({
    objectRole: objectRoleLabel(t, WORKSPACE_DOC_ROLE_CEILING[workspaceRole]),
  });
}

export function getFolderWorkspaceRoleCeilingNotice(
  t: TFunc,
  workspaceRole: Permission,
  roles: readonly FolderRole[]
): string | null {
  const ceiling = WORKSPACE_FOLDER_ROLE_CEILING[workspaceRole];
  if (
    roles.every(role => FOLDER_ROLE_RANK[role] <= FOLDER_ROLE_RANK[ceiling])
  ) {
    return null;
  }
  return t['com.streamlain.permissions.workspace-role-ceiling']({
    objectRole: objectRoleLabel(t, ceiling),
  });
}

export function objectPermissionRoleLabel(
  t: TFunc,
  role?: string | null
): string {
  return role ? objectRoleLabel(t, role) : '';
}

export function createObjectPermissionRoleOptions<Role extends string>(
  t: TFunc,
  roles: readonly Role[]
): PermissionRoleMenuOption<Role>[] {
  return roles.map(role => ({
    role,
    label: objectRoleLabel(t, role),
  }));
}

export function getObjectPermissionAreaBlockedNotice(
  t: TFunc,
  entry: ObjectPermissionAreaBlockedEntry
) {
  if (!entry.isAreaBlocked) {
    return null;
  }

  const areaName = entry.areaBoundaryRootName ?? '';
  if (entry.areaBoundaryKind === 'private') {
    return t['com.streamlain.folder-share.area-blocked.private']({ areaName });
  }
  if (entry.areaBoundaryKind === 'teamspace') {
    return t['com.streamlain.folder-share.area-blocked.teamspace']({
      areaName,
    });
  }
  return t['com.streamlain.folder-share.area-blocked.generic']();
}
